/* eslint-disable no-await-in-loop */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-restricted-syntax */
/* eslint-disable react/jsx-no-bind */
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Step,
	StepLabel,
	Stepper
} from "@mui/material";
import { useRef, useState } from "react";
import MDButton from "components/Basics/MDButton";
import MDBox from "components/Basics/MDBox";
import i18n from "i18n";
import StepImport from "./1. Import";
import { useDispatch } from "react-redux";
import { t } from "i18next";

import sourceActions from "redux-react/actions/sourceActions";
import { createAssets } from "redux-react/reducers/assetReducer";
import { v4 } from "uuid";
import { getSignedUrl, uploadToSignedUrl } from "helpers/s3";
import AddAssetStepSelectFiles from "../components/AddAssetStepSelectFiles";

/**
 * Add sources dialog
 * User import files from different sources
 * @param {*} param0
 * @returns {JSX.Element} AddSourceDialog
 */
export default function AddDocument({
	open,
	onClose,
	format = "documents",
	folderId,
	getFolders = () => {}
}) {
	const dispatch = useDispatch();
	const topContainer = useRef(null);

	/**
	 * Active step
	 */
	const [activeStep, setActiveStep] = useState(0);

	/**
	 * Steps labels
	 */
	const [steps, setSteps] = useState([t("SOURCES.STEPS.upload"), t("SOURCES.STEPS.filesChoice")]);
	/**
	 * Can user go to next step
	 */
	const [stepValid, setStepValid] = useState(false);

	const [skeleton, setSkeleton] = useState({
		selectedRows: [], // Selected rows in table
		files: [], // Imported files
		filesToProcess: [] // {url, name, alreadyExists, image?} files selected by user to process,
	});

	/**
	 * Close dialog
	 */
	function close() {
		setActiveStep(0);
		setStepValid(false);
		setSkeleton({
			selectedRows: [],
			files: [],
			filesToProcess: []
		});
		onClose();
	}

	/**
	 * User can go to next step
	 */
	const validStep = (val = true) => {
		setStepValid(val);
	};

	/**
	 * Go to next step
	 */
	const handleNext = () => {
		setActiveStep(activeStep + 1);
		setStepValid(false);
		topContainer?.current?.scrollIntoView();
	};

	/**
	 * Transform files informations to correct files format
	 * @param {Array<object>} update - New skeleton values
	 */
	const handleSaveFilesSelection = update => {
		setSkeleton(skeleton => {
			return {
				...skeleton,
				...update
			};
		});

		validStep();
		handleNext();
	};

	/**
	 * Update files
	 * @param {Array<object>} files - Updated files
	 */
	const onChangeFiles = files => {
		setSkeleton(skeleton => {
			return {
				...skeleton,
				files
			};
		});
	};

	/**
	 * Upload file to S3
	 * @param {File} file - File to upload
	 * @returns {Promise} - Promise object represents the result of the upload
	 */
	async function uploadFileToS3(file) {
		const path = `sources/${v4()}`;
		const { success, error, result } = await await getSignedUrl(file, {}, path);

		if (!success) {
			return {
				success: false,
				error
			};
		}

		const signedUrl = result.signedUrl;
		const fileObject = result.fileObject;

		await uploadToSignedUrl(signedUrl, file);

		return {
			success: true,
			result: {
				signedUrl,
				fileObject
			}
		};
	}

	const uploadFileToS3Controller = async (localFiles, url) => {
		const localFile = localFiles.find(file => file.name === url);
		const { success, error, result } = await uploadFileToS3(localFile);
		if (success) {
			return result.fileObject;
		} else {
			return {};
		}
	};

	const importDocuments = async () => {
		const localFiles = skeleton.localFiles ?? [];
		//	const files = skeleton.filesToProcess ?? [];
		const files = skeleton.files.filter(file => skeleton.selectedRows.includes(file.url));

		const filesPaylaod = [];

		for (let file of files) {
			const uploadedFile = await uploadFileToS3Controller(localFiles, file.url);

			let payload = {
				name: file.name,
				folderId,
				fileType: format,
				language: file.language,
				alreadyExists: file.alreadyExists,
				content: [uploadedFile],
				metadatas: file.metadatas || {}
			};

			filesPaylaod.push(payload);
		}

		const onSuccessCallback = assets => {
			const formattedAssets = assets.map(asset => ({ ...asset, id: asset._id }));
			dispatch(createAssets(formattedAssets));
			getFolders();
			close();
		};

		dispatch(sourceActions.createManyAssets({ files: filesPaylaod }, onSuccessCallback));
	};

	/**
	 * Get actual step content
	 */
	function getStepContent(stepIndex) {
		switch (stepIndex) {
			case 0: {
				return (
					<StepImport
						format={format}
						skeleton={skeleton}
						handleSave={handleSaveFilesSelection}
						validStep={validStep}
						handleNext={handleNext}
					/>
				);
			}
			case 1: {
				return (
					<AddAssetStepSelectFiles
						format={format}
						skeleton={skeleton}
						setSkeleton={setSkeleton}
						onChange={onChangeFiles}
						validStep={validStep}
					/>
				);
			}
			default:
				return null;
		}
	}

	/**
	 * Get next button according to step
	 * @returns {JSX.Element|null} - Next button or null
	 */
	const getNextButton = () => {
		switch (activeStep) {
			case 0:
				return null;
			case 1: {
				return (
					<MDButton
						disabled={!stepValid || skeleton.selectedRows.length < 1}
						variant="contained"
						color="info"
						onClick={importDocuments}
					>
						{`${t("SETTINGS.add")} (${skeleton.selectedRows.length}) ${t("SOURCES.nextFiles")}`}
					</MDButton>
				);
			}
			default:
				return null;
		}
	};

	return (
		<Dialog
			fullWidth
			maxWidth="xxl"
			PaperProps={{
				sx: {
					height: "90%"
				}
			}}
			open={open}
			onClose={close}
		>
			<DialogTitle>
				<MDBox>
					<Stepper activeStep={activeStep} alternativeLabel>
						{steps.map(label => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
				</MDBox>
			</DialogTitle>
			<DialogContent>
				<div data-id="top-container" ref={topContainer}></div>
				<MDBox mt={2} style={{ height: "90%" }}>
					{getStepContent(activeStep)}
				</MDBox>
			</DialogContent>
			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={close}>
					{i18n.t("SETTINGS.cancel")}
				</MDButton>
				{getNextButton()}
			</DialogActions>
		</Dialog>
	);
}
